import { LiaTransparentElement } from '@liasoft/lit-ui';
import { html, nothing } from 'lit';
import { navigate } from '../../router.js';
import { chooseLogin, linkAccount, loginChoices, pollLogin, setDeveloper } from '../api/developer.js';
import { safeNext } from '../../shared/paths.js';

/**
 * Where a provider sign-in lands when it is not obvious which account to sign in to.
 *
 * Signing in with a provider links nothing and creates nothing. The callback hands the browser a protected,
 * single-use ticket, and this page shows what the identity can do with it: sign in to an account it is
 * linked to, take an address the provider verified (an account that has it is linked, a free address
 * becomes a new account), give a parked account the e-mail it lacks, or add an account for any other address
 * by a link we mail. Every choice but the last answers a single-use code for `login/exchange`; the browser
 * is sent where the answer says, exactly as the callback would have sent it for a single account.
 *
 * The last one - another address - is the same flow as a sign-in link: the tab shows a code, the link is
 * opened anywhere and the code typed there, and this tab's poll is what signs it in.
 *
 * With `needEmail` the identity has no linked account and the provider vouches for no address, so the only
 * thing to do is to enter one.
 */
export class UserLoginChoose extends LiaTransparentElement
{
   static properties = {
      choices: { state: true },
      state: { state: true },
      adding: { state: true },
      error: { state: true },
      busy: { state: true },
      email: { state: true },
      userCode: { state: true }
   };

   constructor()
   {
      super();
      this.ticket = new URLSearchParams(location.search).get('ticket') ?? '';
      this.choices = null;
      this.otherBrowser = false;

      // loading, choosing, sent (a link was mailed), expired (the ticket is spent or too old)
      this.state = 'loading';

      // '' (nothing open), 'new' (an address for a new account) or the id of the parked account being given one.
      this.adding = '';
      this.error = '';
      this.busy = false;
      this.email = '';
      this.userCode = '';
      this.timer = null;
   }

   connectedCallback()
   {
      super.connectedCallback();
      void this.load();
   }

   disconnectedCallback()
   {
      super.disconnectedCallback();

      if (this.timer)
      {
         clearTimeout(this.timer);
         this.timer = null;
      }
   }

   async load()
   {
      if (!this.ticket)
      {
         this.state = 'expired';
         return;
      }

      try
      {
         this.choices = await loginChoices(this.ticket);
         this.state = 'choosing';

         // With nothing to pick from, the address field is the page.
         this.adding = this.choices.needEmail ? 'new' : '';
      }
      catch (error)
      {
         // 403: the ticket is genuine but this is not the browser that signed in with the provider (D31).
         this.otherBrowser = isOtherBrowser(error);
         this.state = 'expired';
      }
   }

   /** Signs in to the choice and leaves for where the service says: a page load, as the callback's redirect was. */
   async choose(body)
   {
      this.busy = true;
      this.error = '';

      try
      {
         const answer = await chooseLogin({ ticket: this.ticket, ...body });

         this.go(answer.next);
      }
      catch (error)
      {
         if (error.status === 400 || isOtherBrowser(error))
         {
            this.otherBrowser = isOtherBrowser(error);
            this.state = 'expired';
         }
         else
         {
            this.error = error.message;
         }

         this.busy = false;
      }
   }

   /** A page load, as the callback's redirect was. Its own method so a test can watch where it goes. */
   go(url)
   {
      location.assign(url);
   }

   async sendLink(event, principalId)
   {
      event.preventDefault();

      const address = String(new FormData(event.target).get('email') ?? '').trim();

      if (!address)
      {
         return;
      }

      this.busy = true;
      this.error = '';

      try
      {
         const started = await linkAccount({ ticket: this.ticket, email: address, principalId });

         this.email = address;
         this.userCode = started.userCode ?? '';
         this.state = 'sent';
         this.poll(started.deviceCode);
      }
      catch (error)
      {
         if ((error.status === 400 && /expired/i.test(error.message)) || isOtherBrowser(error))
         {
            this.otherBrowser = isOtherBrowser(error);
            this.state = 'expired';
         }
         else
         {
            this.error = error.message;
         }
      }
      finally
      {
         this.busy = false;
      }
   }

   /** Asks until the link is opened; `setTimeout` chained so a slow answer cannot overlap the next request. */
   poll(deviceCode, delay = POLL_MS)
   {
      this.timer = setTimeout(async () =>
      {
         try
         {
            const status = await pollLogin(deviceCode);

            if (status.state === 'complete' && status.session)
            {
               setDeveloper({
                  token: status.session,
                  principalId: status.principalId,
                  email: status.email ?? this.email,
                  expiresAt: status.expiresAt
               });

               // A client's code is finished in the browser it signed in: its page continues from here, on
               // the team step, because the person has just chosen the account.
               navigate(
                  this.choices?.deviceCode
                     ? `/device?code=${encodeURIComponent(this.choices.deviceCode)}&step=team`
                     : safeNext(this.choices?.next) ?? '/',
                  { replace: true });
               return;
            }

            if (status.state === 'denied')
            {
               this.state = 'choosing';
               this.error = status.message
                  || 'That address could not be added to this sign-in. Try another address, or sign in again.';
               return;
            }

            if (status.state === 'expired')
            {
               this.state = 'choosing';
               this.error = 'That link ran out before it was opened. Ask for a new one.';
               return;
            }
         }
         catch
         {
            // A failed poll is not a failed sign-in; the flow expires on its own.
         }

         this.poll(deviceCode);
      }, delay);
   }

   render()
   {
      const choices = this.choices;

      return html`
         <lia-auth-layout
            .showThemeToggle=${false}
            brand="covdbg"
            brand-icon="fa-solid fa-bug"
            heading=${this.state === 'sent' ? 'Check your mail' : 'Choose an account'}
            description=${this.state === 'choosing' && choices && !choices.needEmail
               ? `Signed in with ${providerName(choices)} as ${choices.displayName}. Choose an account:`
               : ''}
            error=${this.error}
            max-width="520"
         >
            ${this.state === 'loading'
               ? html`
                  <p class="text-body-secondary small text-center mb-0">
                     <lia-spinner size="sm" class="me-2"></lia-spinner>One moment
                  </p>`
               : nothing}
            ${this.state === 'expired' ? this.expired : nothing}
            ${this.state === 'sent' ? this.sent : nothing}
            ${this.state === 'choosing' ? this.chooser : nothing}
         </lia-auth-layout>`;
   }

   get expired()
   {
      return html`
         <lia-alert
            variant="warning"
            icon="fa-solid fa-triangle-exclamation"
            message=${this.otherBrowser
               ? 'This sign-in was completed in another browser, so it cannot be used here. Sign in again in this one.'
               : 'This sign-in has expired, or was already used. Sign in again.'}
         ></lia-alert>
         <a class="btn btn-outline-secondary w-100 mt-3" href=${this.choices?.deviceCode ? `/device?code=${encodeURIComponent(this.choices.deviceCode)}` : '/login'}>Start again</a>`;
   }

   get sent()
   {
      return html`
         <div class="text-center py-2">
            <i class="fa-solid fa-envelope-open-text fa-2x text-primary mb-3" aria-hidden="true"></i>
            <p class="mb-1">We sent a link to <strong>${this.email}</strong>.</p>
            <p class="text-body-secondary small mb-2">Open it and enter this code there</p>
            <p class="display-6 font-monospace user-select-all mb-3" data-user-code>${this.userCode}</p>
            <p class="text-body-secondary small mb-0">
               <lia-spinner size="sm" class="me-2"></lia-spinner>Waiting for the link
            </p>
         </div>`;
   }

   get chooser()
   {
      const { needEmail, linked = [], parked = [], candidates = [], preselected } = this.choices;

      if (needEmail)
      {
         return html`
            <p class="text-body-secondary">
               ${providerName(this.choices)} shares no verified e-mail. Enter the address for your covdbg account;
               we send a link to confirm it.
            </p>
            ${this.emailForm(undefined)}`;
      }

      return html`
         ${linked.length
            ? html`
               <div class="list-group mb-3" data-linked>
                  ${linked.map(account => html`
                     <button
                        type="button"
                        class="list-group-item list-group-item-action"
                        ?disabled=${this.busy}
                        @click=${() => this.choose({ principalId: account.principalId })}
                     >
                        <div class="fw-semibold">${account.email}</div>
                        ${account.displayName && account.displayName !== account.email
                           ? html`<div class="small text-body-secondary">${account.displayName}</div>`
                           : nothing}
                     </button>`)}
               </div>`
            : nothing}

         ${candidates.length
            ? html`
               <p class="small text-body-secondary mb-2">
                  ${linked.length ? 'Or use an address' : 'Use an address'} ${providerName(this.choices)} verified for you:
               </p>
               <div class="list-group mb-3" data-candidates>
                  ${candidates.map(candidate => html`
                     <button
                        type="button"
                        class=${`list-group-item list-group-item-action${candidate.address === preselected ? ' list-group-item-primary' : ''}`}
                        data-candidate=${candidate.address}
                        ?disabled=${this.busy}
                        @click=${() => this.choose({ address: candidate.address })}
                     >
                        <div class="d-flex justify-content-between align-items-center gap-2">
                           <span class="fw-semibold">${candidate.address}</span>
                           <span>
                              ${candidate.address === preselected ? html`<span class="badge text-bg-primary me-1">Suggested</span>` : nothing}
                              <span class=${`badge ${candidate.existing ? 'text-bg-secondary' : 'text-bg-success'}`}>${candidate.existing ? 'existing account' : 'new account'}</span>
                           </span>
                        </div>
                        <div class="small text-body-secondary">${providerName(this.choices)} verified this address</div>
                     </button>`)}
               </div>`
            : nothing}

         ${parked.map(account => html`
            <div class="border rounded p-3 mb-3" data-parked=${account.principalId}>
               <div class="d-flex justify-content-between align-items-center gap-2">
                  <div>
                     <div class="fw-semibold">${account.displayName || 'An earlier account'}</div>
                     <div class="small text-body-secondary">needs an e-mail</div>
                  </div>
                  <button
                     type="button"
                     class="btn btn-outline-secondary btn-sm"
                     ?disabled=${this.busy}
                     @click=${() => { this.adding = this.adding === account.principalId ? '' : account.principalId; }}
                  >Add an e-mail</button>
               </div>
               ${this.adding === account.principalId ? html`<div class="mt-3">${this.emailForm(account.principalId)}</div>` : nothing}
            </div>`)}

         <button
            type="button"
            class="btn btn-outline-secondary w-100"
            data-add-account
            ?disabled=${this.busy}
            @click=${() => { this.adding = this.adding === 'new' ? '' : 'new'; }}
         >
            <i class="fa-solid fa-plus me-2" aria-hidden="true"></i>Add an account with another e-mail
         </button>
         ${this.adding === 'new' ? html`<div class="mt-3">${this.emailForm(undefined)}</div>` : nothing}`;
   }

   /** The address field. `principalId` names the parked account that gets it as its first e-mail. */
   emailForm(principalId)
   {
      return html`
         <form @submit=${event => this.sendLink(event, principalId)}>
            <label class="form-label" for="choose-email">E-mail address</label>
            <input
               id="choose-email"
               class="form-control mb-2"
               type="email"
               name="email"
               autocomplete="email"
               placeholder="you@example.com"
               required
               ?disabled=${this.busy}
            >
            <div class="form-text mb-2">We send a link to confirm it. The address becomes the account's e-mail, and it does not change.</div>
            <button class="btn btn-primary w-100" type="submit" ?disabled=${this.busy}>
               ${this.busy ? html`<lia-spinner size="sm" class="me-2"></lia-spinner>Sending` : 'Send me a link'}
            </button>
         </form>`;
   }
}

/** The provider as a person names it, with the instance where it is not the default one. */
function providerName(choices)
{
   const name = PROVIDER_NAMES[choices.provider] ?? choices.provider;

   return choices.issuer ? `${name} (${choices.issuer})` : name;
}

const PROVIDER_NAMES = { github: 'GitHub' };

/** How often the tab asks whether the link was opened. */
const POLL_MS = 3000;

customElements.define('user-login-choose', UserLoginChoose);

/** 403 with this wording is the service saying the ticket is genuine but belongs to another browser (D31); other 403s are refusals of a choice. */
function isOtherBrowser(error)
{
   return error?.status === 403 && /another browser/i.test(error.message ?? '');
}
